<script setup>
import { BModal, BPopover, BTable } from 'bootstrap-vue-next';
import { computed } from 'vue';

import { numberFormatter, numberProps } from '~/common/FieldFormats';

const props = defineProps({
  event: {
    type: Object,
    default: null,
  },
});

const model = defineModel({ type: Boolean });

const numberPropsDefaultZero = {
  ...numberProps,
  formatter: ({ value }) => numberFormatter(value ?? 0),
};

const nonCodePointFields = ['plan_points', 'engage_points', 'discuss_points', 'translate_points'];

const contributors = computed(() => (props.event?.contributors ?? []).map((contributor) => {
  const points = Object.fromEntries(nonCodePointFields.map(key => [key, contributor[key] ?? 0]));

  return {
    ...contributor,
    ...points,
    non_code_points: Object.values(points).reduce((sum, value) => sum + value, 0),
  };
}));

const contributorFields = [
  { key: 'username', label: 'Username' },
  { key: 'opened_merge_requests', label: 'Opened MRs', ...numberPropsDefaultZero },
  { key: 'merged_merge_requests', label: 'Merged MRs', ...numberPropsDefaultZero },
  { key: 'merged_with_issues', label: 'Merged with Issues', ...numberPropsDefaultZero },
  {
    key: 'non_code_points',
    label: 'Non-code',
    ...numberPropsDefaultZero,
    popover: [
      { label: 'Plan', field: 'plan_points' },
      { label: 'Engage', field: 'engage_points' },
      { label: 'Discuss', field: 'discuss_points' },
      { label: 'Translate', field: 'translate_points' },
    ],
  },
  { key: 'score', label: 'Score', ...numberPropsDefaultZero },
];
</script>

<template>
  <BModal
    v-model="model"
    :title="event?.title || 'Event Details'"
    size="xl"
    scrollable
    no-footer
  >
    <div v-if="event">
      <div>
        <strong>All participants:</strong> {{ event.contributors?.length || 0 }}
        <br>
        <strong>Prize winners:</strong> {{ event.prizeWinnersCount || 0 }}
        <br>
        <strong>All merge requests opened:</strong> {{ event.openedMRs || 0 }}
        <br>
        <strong>Merge requests merged:</strong> {{ event.mergedMRs || 0 }}
        <br>
        <strong>Merged merge requests with linked issues:</strong> {{ event.mergedWithIssues || 0 }}
        <br>
        <strong>Percent merge requests merged:</strong> {{ event.mergeRate || '0.00' }}%
      </div>

      <div v-if="event.contributors?.length > 0">
        <h5 class="mt-4">Participants</h5>
        <BTable
          :items="contributors"
          :fields="contributorFields"
          responsive
          small
          hover
        >
          <template
            v-for="field in contributorFields.filter(f => f.popover)"
            :key="field.key"
            #[`cell(${field.key})`]="{ item, unformatted, value }"
          >
            <template v-if="unformatted === 0">0</template>
            <template v-else>
              <BPopover
                :close-on-hide="true"
                :delay="{ show: 0, hide: 0 }"
                :tooltip="true"
              >
                <template #target>
                  <span class="with-help-text">{{ value }}</span>
                </template>
                <template
                  v-for="figure in field.popover"
                  :key="figure.field"
                >
                  <span>
                    {{ figure.label }}: {{ item[figure.field] }}
                  </span>
                  <br>
                </template>
              </BPopover>
            </template>
          </template>
        </BTable>
      </div>
    </div>
  </BModal>
</template>
